import clsx from 'clsx'
import { MISSION_CALCULATING_STATUSES, PATH } from 'utils/constants'
import { dispatchBlockReason } from 'utils/robotDispatch'
import { getScenarioLaunchBlockReasonTitle } from 'utils/scenarioReadiness'

import style from './ScenariosList.module.css'

interface IScenario {
  id: number
  name: string
  missions_ids: number[]
  missions_statuses: string[]
  status?: string
  missions_type_inconsistent?: boolean
  service_points_incomplete?: boolean
  robot_type?: string | null
  maintenance?: { service_points?: Array<{ kind?: string | null; lot_model?: string | null }> | null } | null
}

const ScenarioModalItem = ({
  scenario,
  selected,
  onSelect,
  disabled,
  targetRobotType,
}: {
  scenario: IScenario
  selected: boolean
  onSelect: () => void
  disabled?: boolean
  targetRobotType?: string
}) => {
  const blockTitle = disabled
    ? getScenarioLaunchBlockReasonTitle(
        {
          missions_statuses: scenario.missions_statuses,
          missions_type_inconsistent: scenario.missions_type_inconsistent,
          service_points_incomplete: scenario.service_points_incomplete,
          robot_type: scenario.robot_type,
          maintenance: scenario.maintenance,
        },
        targetRobotType
      )
    : undefined
  return (
    <div
      className={clsx(style.tableItem, selected && style.selected, disabled && style.disabled)}
      onClick={() => {
        if (!disabled) onSelect()
      }}
      title={blockTitle}
    >
      <span>{scenario.id}</span>
      <span className={style.colLeftMultiline}>{scenario.name}</span>
      <span className={style.colLeftMultiline}>{scenario.missions_ids?.join(', ')}</span>
      <a className={style.link} href={`${PATH.SCENARIOS}${scenario.id}`} target="_blank" rel="noreferrer">
        Show
      </a>
    </div>
  )
}

const ScenariosList = ({
  scenarios,
  selectedScenario,
  onSelect,
  onScrollBottom,
  allowEmptyScenarios = false,
  hideInconsistentScenarios = false,
  targetRobotType,
}: {
  scenarios: IScenario[]
  selectedScenario: IScenario | undefined
  onSelect: (scenario: IScenario) => void
  onScrollBottom?: () => void
  allowEmptyScenarios?: boolean
  /** When true, do not show scenarios with different mission types (for task assignment) */
  hideInconsistentScenarios?: boolean
  /** Selected robot. Rows whose stations that robot cannot use stay visible but disabled. */
  targetRobotType?: string
}) => {
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      onScrollBottom?.()
    }
  }

  // Only show scenarios where all missions have status OK (or scenarios that have no missions yet, if allowed)
  // When hideInconsistentScenarios, exclude scenarios with mixed mission types (e.g. for task assignment)
  const filteredScenarios = scenarios.filter(scenario => {
    if (hideInconsistentScenarios && scenario.missions_type_inconsistent) return false
    const isEmpty = !scenario.missions_statuses || scenario.missions_statuses.length === 0
    if (isEmpty) return allowEmptyScenarios
    return scenario.missions_statuses.every(s => s === MISSION_CALCULATING_STATUSES.OK)
  })
  return (
    <>
      <div className={style.table} onScroll={handleScroll}>
        <div className={style.tableHeader}>
          <span>ID</span>
          <span className={style.scenarioName}>Name</span>
          <span className={style.scenarioName}>Missions</span>
          <span>Link</span>
        </div>
        {filteredScenarios.map(scenario => {
          const stationBlock = targetRobotType
            ? dispatchBlockReason({
                entityType: scenario.robot_type,
                robotType: targetRobotType,
                maintenance: scenario.maintenance,
              })
            : undefined
          return (
            <ScenarioModalItem
              key={scenario.id}
              scenario={scenario}
              selected={scenario?.id === selectedScenario?.id}
              onSelect={() => onSelect(scenario)}
              targetRobotType={targetRobotType}
              disabled={Boolean(scenario.service_points_incomplete) || Boolean(stationBlock)}
            />
          )
        })}
      </div>
    </>
  )
}

export default ScenariosList
